import { SelectionList } from '@dixa/lib-shared-habla';
import { useFormContext } from 'react-hook-form';

import { webformEndpointsApi } from 'apis/webformEndpoints';
import { CustomForm } from 'app/views/layout/views/settings/views/customForms';
import { useCustomForms } from 'app/views/layout/views/settings/views/customForms/hooks/useCustomForms/useCustomForms';
import { HookFormSelectionListSingle } from 'components/hookForm';

import { FormValues, messengerSettingsFormFields } from '../../types';

export function FormSelector({ forms }: { forms: CustomForm[] }): JSX.Element {
  const { setValue } = useFormContext<FormValues>();
  const [readFormEndpoint] =
    webformEndpointsApi.endpoints.readWebformEndpoints.useLazyQuery();
  const { getSingleForm } = useCustomForms();

  const validate = async (formId: string): Promise<string | true> => {
    const formDetailsPromise = getSingleForm(formId);

    const formEndpointsPromise = readFormEndpoint(
      { formIntegrationId: formId },
      true,
    );

    try {
      const [formEndpointResults, formDetailsResult] = await Promise.all([
        formEndpointsPromise,
        formDetailsPromise,
      ]);

      const endpoints = formEndpointResults.data?.data.endpoints || [];
      const formFields = formDetailsResult.ok
        ? formDetailsResult.value.fields ?? []
        : [];

      if (formFields.length === 0) {
        return 'The form you selected does not have any fields. Please select a form with fields.';
      }

      if (!formFields.some((field) => field.type === 'email')) {
        return 'You need to select a form with an email field.';
      }

      if (endpoints.length === 0) {
        return 'No endpoints defined for this form. Create an endpoint in the Forms builder.';
      }
    } catch (error) {
      return 'Failed to validate';
    }

    return true;
  };

  const formItems = forms.map((form) => ({
    id: form.id,
    name: form.title,
  }));

  function resetEndpointField() {
    // When this field changes, we need to reset the endpoint field because endpoints are form-specific
    setValue(messengerSettingsFormFields.formOnlyModeEndpointURI, undefined);
  }

  return (
    <HookFormSelectionListSingle
      label="Form"
      name={messengerSettingsFormFields.formOnlyModeContactFormId}
      rules={{ validate, required: 'Please select a form' }}
      componentProps={{
        onChange: resetEndpointField,
        getItemTitleByValue: (itemValue) =>
          formItems.find((item) => item.id === itemValue)?.name,
      }}
    >
      {formItems.map((item) => (
        <SelectionList.Item key={item.id} value={item.id}>
          {item.name}
        </SelectionList.Item>
      ))}
    </HookFormSelectionListSingle>
  );
}
